<template>
  <div class="guild-view">
    <div class="guild-window">
      <header class="guild-header">
        <div>
          <p class="guild-kicker">Houses of Celestia</p>
          <h1>{{ headerTitle }}</h1>
        </div>
        <div class="guild-gold">
          <span class="gold-label">Gold</span>
          <span class="gold-value">{{ formatNumber(playerGold) }}</span>
        </div>
      </header>

      <nav class="guild-tabs" aria-label="Guild sections">
        <button
          v-if="guildStore.isMember"
          type="button"
          class="guild-tab"
          :class="{ active: tab === 'hall' }"
          @click="tab = 'hall'"
        >
          Guild Hall
        </button>
        <button
          type="button"
          class="guild-tab"
          :class="{ active: tab === 'directory' }"
          @click="openDirectory"
        >
          Guild Directory
        </button>
        <button
          v-if="!guildStore.isMember"
          type="button"
          class="guild-tab"
          :class="{ active: tab === 'charters' }"
          @click="openCharters"
        >
          Founding Charters
        </button>
        <button
          v-if="!guildStore.isMember"
          type="button"
          class="guild-tab"
          :class="{ active: tab === 'start' }"
          @click="tab = 'start'"
        >
          Start Charter
        </button>
      </nav>

      <p v-if="flashMessage" :class="['flash', flashKind]">{{ flashMessage }}</p>
      <p v-else-if="guildStore.loadError" class="flash error">
        {{ guildStore.loadError }}
      </p>

      <section v-if="tab === 'hall' && hall" class="guild-body">
        <div class="hall-lede">
          <h2>{{ hall.name }}</h2>
          <p class="lede-copy">{{ hall.description || "No charter text on file." }}</p>
          <p class="meta-line">
            Leader
            <strong>{{ hall.leader?.nickname || "Unknown" }}</strong>
            · {{ hall.member_count || hall.members?.length || 0 }} members
            · Your rank
            <strong>{{ roleLabel(hall.role || hall.my_role) }}</strong>
            · Guild Rank
            <strong>#{{ hall.guild_rank || "—" }}</strong>
            · {{ formatNetWorth(hall.net_worth) }}
          </p>
        </div>

        <div
          v-if="guildStore.canModerate && pendingJoins.length"
          class="inbox-panel"
        >
          <h3>Join Requests</h3>
          <ul class="person-list">
            <li v-for="req in pendingJoins" :key="req.id">
              <div class="person-id">
                <AvatarPortrait :avatar-id="req.avatar_id" :size="40" />
                <div>
                  <strong>{{ req.nickname || "Unknown" }}</strong>
                  <span class="race-badge" :style="raceStyle(req.race)">{{
                    raceLabel(req.race)
                  }}</span>
                </div>
              </div>
              <div class="row-actions">
                <button
                  type="button"
                  class="guild-btn gold"
                  :disabled="busy"
                  @click="onResolveJoin(req.user_id, 'accept')"
                >
                  Accept
                </button>
                <button
                  type="button"
                  class="guild-btn ghost"
                  :disabled="busy"
                  @click="onResolveJoin(req.user_id, 'reject')"
                >
                  Reject
                </button>
              </div>
            </li>
          </ul>
        </div>

        <h3>Roster</h3>
        <table class="guild-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Race</th>
              <th>Rank</th>
              <th>Empire Net Worth</th>
              <th>Status</th>
              <th v-if="guildStore.canModerate">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!hallMembers.length">
              <td :colspan="hallColspan">No members listed.</td>
            </tr>
            <tr v-for="member in hallMembers" :key="member.user_id">
              <td>
                <div class="person-id">
                  <AvatarPortrait :avatar-id="member.avatar_id" :size="36" />
                  <strong>{{ member.nickname || "Unknown" }}</strong>
                </div>
              </td>
              <td>
                <span class="race-badge" :style="raceStyle(member.race)">{{
                  raceLabel(member.race)
                }}</span>
              </td>
              <td>{{ roleLabel(member.role) }}</td>
              <td>{{ formatNetWorth(member.empire_net_worth) }}</td>
              <td>
                <span
                  class="online-pip"
                  :class="{ on: memberIsOnline(member) }"
                  :title="memberIsOnline(member) ? 'Online' : 'Away'"
                ></span>
                {{ member.status || "Away" }}
              </td>
              <td v-if="guildStore.canModerate">
                <button
                  v-if="canKickMember(member)"
                  type="button"
                  class="guild-btn danger"
                  :disabled="busy"
                  @click="askKick(member)"
                >
                  Kick
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <footer class="hall-footer">
          <button
            type="button"
            class="guild-btn danger"
            :disabled="busy"
            @click="confirmKind = 'leave'"
          >
            Leave Guild
          </button>
        </footer>
      </section>

      <section v-else-if="tab === 'directory'" class="guild-body">
        <div class="filter-bar">
          <input
            v-model="searchText"
            class="filter-search"
            type="search"
            placeholder="Search guilds by name…"
            @keyup.enter="runSearch"
          />
          <button type="button" class="guild-btn" :disabled="busy" @click="runSearch">
            Search
          </button>
        </div>

        <div v-if="guildStore.isLoading && !guildStore.guilds.length" class="empty">
          Consulting the registry…
        </div>
        <div v-else-if="!guildStore.guilds.length" class="empty">
          No official guilds match that search.
        </div>

        <ul v-else class="card-list">
          <li
            v-for="guild in guildStore.guilds"
            :key="guild.id"
            class="list-card"
            :class="{ selected: selectedGuildId === guild.id }"
            @click="openGuild(guild.id)"
          >
            <div>
              <h3>{{ guild.name }}</h3>
              <p>{{ guild.description || "No description." }}</p>
              <p class="meta-line">
                Leader {{ guild.leader?.nickname || "Unknown" }}
                · {{ guild.member_count }} members
                · Rank #{{ guild.guild_rank || "—" }}
                · {{ formatNumber(guild.net_worth) }} Net Worth
              </p>
            </div>
            <i class="fas fa-chevron-right"></i>
          </li>
        </ul>

        <div v-if="guildStore.guildTotalPages > 1" class="pager">
          <button
            type="button"
            class="guild-btn ghost"
            :disabled="guildStore.guildPage <= 1 || busy"
            @click="changePage(guildStore.guildPage - 1)"
          >
            Prev
          </button>
          <span>Page {{ guildStore.guildPage }} / {{ guildStore.guildTotalPages }}</span>
          <button
            type="button"
            class="guild-btn ghost"
            :disabled="guildStore.guildPage >= guildStore.guildTotalPages || busy"
            @click="changePage(guildStore.guildPage + 1)"
          >
            Next
          </button>
        </div>
      </section>

      <section v-else-if="tab === 'charters'" class="guild-body">
        <div class="filter-bar">
          <input
            v-model="charterSearchText"
            class="filter-search"
            type="search"
            placeholder="Search charters by name or founder…"
          />
        </div>

        <div v-if="guildStore.myCharter" class="inbox-panel">
          <h3>Your Charter</h3>
          <p>{{ guildStore.myCharter.name }} — {{ guildStore.myCharter.my_status || "creator" }}</p>
          <button
            type="button"
            class="guild-btn gold"
            @click="openCharter(guildStore.myCharter.id)"
          >
            Open
          </button>
        </div>

        <div v-if="guildStore.isLoading && !guildStore.charters.length" class="empty">
          Unrolling the founding ledgers…
        </div>
        <div v-else-if="!guildStore.charters.length" class="empty">
          No open charters. Start one to gather founders.
        </div>

        <ul v-else class="card-list">
          <li
            v-for="charter in guildStore.charters"
            :key="charter.id"
            class="list-card"
            @click="openCharter(charter.id)"
          >
            <div class="card-wide">
              <h3>{{ charter.name }}</h3>
              <p>{{ charter.description || "No description." }}</p>
              <p class="meta-line">
                {{ charter.creator?.nickname || "Unknown" }}
                · {{ raceLabel(charter.creator?.race) }}
              </p>
              <div class="bars">
                <div class="bar-block">
                  <span>Gold {{ formatNumber(charter.gold_collected) }} / {{ formatNumber(charter.gold_target) }}g</span>
                  <div class="bar">
                    <div class="bar-fill gold" :style="{ width: goldPct(charter) }"></div>
                  </div>
                </div>
                <div class="bar-block">
                  <span
                    >{{ charter.accepted_signers }} / {{ charter.max_signers }} founders (min
                    {{ charter.min_signers }})</span
                  >
                  <div class="bar">
                    <div
                      class="bar-fill cyan"
                      :style="{ width: signerPct(charter) }"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="row-actions charter-card-actions" @click.stop>
                <button
                  v-if="canSign(charter)"
                  type="button"
                  class="guild-btn"
                  :disabled="busy"
                  @click="openSignModal(charter.id)"
                >
                  Sign Charter
                </button>
                <button
                  v-if="canContribute(charter)"
                  type="button"
                  class="guild-btn gold"
                  :disabled="busy"
                  @click="openCharter(charter.id)"
                >
                  Contribute
                </button>
              </div>
            </div>
          </li>
        </ul>

        <div v-if="guildStore.charterTotalPages > 1" class="pager">
          <button
            type="button"
            class="guild-btn ghost"
            :disabled="guildStore.charterPage <= 1 || busy"
            @click="changeCharterPage(guildStore.charterPage - 1)"
          >
            Prev
          </button>
          <span>Page {{ guildStore.charterPage }} / {{ guildStore.charterTotalPages }}</span>
          <button
            type="button"
            class="guild-btn ghost"
            :disabled="guildStore.charterPage >= guildStore.charterTotalPages || busy"
            @click="changeCharterPage(guildStore.charterPage + 1)"
          >
            Next
          </button>
        </div>
      </section>

      <section v-else-if="tab === 'start'" class="guild-body">
        <p class="charter-notice">
          Notice: Charters remain open for 7 days. If cancelled by the creator, 50% of
          contributed gold is refunded. If the charter expires without being founded or
          cancelled, 100% of contributed gold is lost.
        </p>
        <p class="lede-copy">
          Declare a name, write the charter, and optionally pledge gold toward the
          founding purse ({{ formatNumber(foundingTarget) }}g in this realm).
        </p>
        <form class="start-form" @submit.prevent="onCreate">
          <label>
            Guild name
            <input v-model="formName" maxlength="50" required />
          </label>
          <label>
            Description
            <textarea v-model="formDescription" maxlength="500" rows="5"></textarea>
          </label>
          <label>
            Initial pledge (gold)
            <input
              v-model.number="formGold"
              type="number"
              min="0"
              :max="playerGold"
            />
          </label>
          <button
            type="submit"
            class="guild-btn gold"
            :disabled="busy || !formName.trim()"
          >
            Declare Charter
          </button>
        </form>
      </section>
    </div>

    <Teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="guildStore.selectedCharter && charterModalOpen"
          class="detail-backdrop"
          @click.self="closeCharter"
        >
          <div class="detail-container" role="dialog" aria-modal="true">
            <header class="detail-header">
              <h2>{{ selectedCharter.name }}</h2>
              <button type="button" class="close-btn" aria-label="Close" @click="closeCharter">
                ✕
              </button>
            </header>
            <div class="detail-body">
              <p class="lede-copy">
                {{ selectedCharter.description || "No description." }}
              </p>
              <p class="meta-line">
                Creator {{ selectedCharter.creator?.nickname || "Unknown" }}
                · {{ raceLabel(selectedCharter.creator?.race) }}
              </p>
              <div class="bars">
                <div class="bar-block">
                  <span
                    >{{ formatNumber(selectedCharter.gold_collected) }} /
                    {{ formatNumber(selectedCharter.gold_target) }}g</span
                  >
                  <div class="bar">
                    <div
                      class="bar-fill gold"
                      :style="{ width: goldPct(selectedCharter) }"
                    ></div>
                  </div>
                </div>
                <div class="bar-block">
                  <span
                    >{{ selectedCharter.accepted_signers }} /
                    {{ selectedCharter.max_signers }} founders (min
                    {{ selectedCharter.min_signers }})</span
                  >
                  <div class="bar">
                    <div
                      class="bar-fill cyan"
                      :style="{ width: signerPct(selectedCharter) }"
                    ></div>
                  </div>
                </div>
              </div>

              <table class="guild-table">
                <thead>
                  <tr>
                    <th>Signer</th>
                    <th>Race</th>
                    <th>Status</th>
                    <th>Gold</th>
                    <th v-if="isCharterCreator"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="signer in charterSigners" :key="signer.id">
                    <td>
                      <div class="person-id">
                        <AvatarPortrait :avatar-id="signer.avatar_id" :size="36" />
                        <strong>{{ signer.nickname || "Unknown" }}</strong>
                      </div>
                    </td>
                    <td>
                      <span class="race-badge" :style="raceStyle(signer.race)">{{
                        raceLabel(signer.race)
                      }}</span>
                    </td>
                    <td>{{ signer.status }}</td>
                    <td>{{ formatNumber(signer.gold_contributed) }}</td>
                    <td v-if="isCharterCreator">
                      <div
                        v-if="
                          signer.status === 'pending' &&
                          signer.user_id !== selectedCharter.creator_id
                        "
                        class="row-actions"
                      >
                        <button
                          type="button"
                          class="guild-btn gold"
                          :disabled="busy"
                          @click="onResolveSigner(signer.user_id, 'accept')"
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          class="guild-btn ghost"
                          :disabled="busy"
                          @click="onResolveSigner(signer.user_id, 'reject')"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="canContribute(selectedCharter)" class="contribute-row">
                <input
                  v-model.number="contributeGold"
                  type="number"
                  min="1"
                  :max="playerGold"
                />
                <button
                  type="button"
                  class="guild-btn gold"
                  :disabled="busy || !contributeGold"
                  @click="onContribute"
                >
                  Contribute
                </button>
              </div>

              <footer class="detail-footer">
                <button
                  v-if="canSign(selectedCharter)"
                  type="button"
                  class="guild-btn"
                  :disabled="busy"
                  @click="openSignModal(selectedCharter.id)"
                >
                  Sign Charter
                </button>
                <button
                  v-if="isCharterCreator"
                  type="button"
                  class="guild-btn gold"
                  :disabled="busy || !canFound"
                  @click="confirmKind = 'found'"
                >
                  Incorporate Guild
                </button>
                <button
                  v-if="isCharterCreator"
                  type="button"
                  class="guild-btn danger"
                  :disabled="busy"
                  @click="confirmKind = 'cancel'"
                >
                  Cancel Charter
                </button>
              </footer>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <Teleport to="body">
      <transition name="modal-fade">
        <div
          v-if="selectedGuildDetail && guildModalOpen"
          class="detail-backdrop"
          @click.self="closeGuild"
        >
          <div class="detail-container" role="dialog" aria-modal="true">
            <header class="detail-header">
              <h2>{{ selectedGuildDetail.name }}</h2>
              <button type="button" class="close-btn" aria-label="Close" @click="closeGuild">
                ✕
              </button>
            </header>
            <div class="detail-body">
              <p class="lede-copy">
                {{ selectedGuildDetail.description || "No description." }}
              </p>
              <p class="meta-line">
                Leader {{ selectedGuildDetail.leader?.nickname || "Unknown" }}
                · {{ selectedGuildDetail.member_count }} members
                · Rank #{{ selectedGuildDetail.guild_rank || "—" }}
                ({{ formatNetWorth(selectedGuildDetail.net_worth) }})
              </p>
              <table class="guild-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Rank</th>
                    <th v-if="previewIsOwnGuild">Empire Net Worth</th>
                    <th v-if="previewIsOwnGuild">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!(selectedGuildDetail.members || []).length">
                    <td :colspan="previewColspan">No members listed.</td>
                  </tr>
                  <tr
                    v-for="member in selectedGuildDetail.members || []"
                    :key="member.user_id"
                  >
                    <td>
                      <div class="person-id">
                        <AvatarPortrait :avatar-id="member.avatar_id" :size="36" />
                        <div>
                          <strong>{{ member.nickname || "Unknown" }}</strong>
                          <span class="race-badge" :style="raceStyle(member.race)">{{
                            raceLabel(member.race)
                          }}</span>
                        </div>
                      </div>
                    </td>
                    <td>{{ roleLabel(member.role) }}</td>
                    <td v-if="previewIsOwnGuild">
                      {{ formatNetWorth(member.empire_net_worth) }}
                    </td>
                    <td v-if="previewIsOwnGuild">
                      <span
                        class="online-pip"
                        :class="{ on: memberIsOnline(member) }"
                      ></span>
                      {{ member.status || "Away" }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <footer class="detail-footer">
                <button
                  v-if="canRequestJoin"
                  type="button"
                  class="guild-btn gold"
                  :disabled="busy"
                  @click="onJoin"
                >
                  Request to Join
                </button>
                <p v-else-if="selectedGuildDetail.my_join_status === 'pending'" class="meta-line">
                  Join request pending.
                </p>
                <p v-else-if="guildStore.isMember" class="meta-line">
                  You already belong to a guild.
                </p>
              </footer>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>

    <Modal
      :is-visible="Boolean(confirmKind)"
      :title="confirmTitle"
      :message="confirmMessage"
      :confirm-label="confirmLabel"
      @confirm="onConfirm"
      @cancel="clearConfirm"
    />

    <Modal
      :is-visible="signModalOpen"
      title="Sign Charter"
      confirm-label="Sign"
      @confirm="onSignConfirm"
      @cancel="closeSignModal"
    >
      <p>
        Optionally pledge gold toward this founding purse. Pledged gold is held in escrow
        until the charter is founded, cancelled, or your petition is resolved.
      </p>
      <label class="sign-pledge-label">
        Initial Gold Pledge
        <input
          v-model.number="signPledge"
          type="number"
          min="0"
          :max="playerGold"
        />
      </label>
    </Modal>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useAuthStore } from "../store/auth";
import { useGameStore } from "../store/game";
import { useGuildStore } from "../store/guild";
import AvatarPortrait from "../components/AvatarPortrait.vue";
import Modal from "../components/Modal.vue";
import { getRaceColor } from "../utils/raceColors";
import { projectResourceStocks } from "../utils/resourceDisplay";

const authStore = useAuthStore();
const gameStore = useGameStore();
const guildStore = useGuildStore();

const tab = ref("directory");
const busy = ref(false);
const flashMessage = ref("");
const flashKind = ref("info");
const searchText = ref("");
const charterSearchText = ref("");
const formName = ref("");
const formDescription = ref("");
const formGold = ref(0);
const contributeGold = ref(10);
const charterModalOpen = ref(false);
const guildModalOpen = ref(false);
const confirmKind = ref("");
const kickTarget = ref(null);
const signModalOpen = ref(false);
const signCharterId = ref(null);
const signPledge = ref(0);
const projectedGold = ref(0);
let goldTicker = null;
let charterSearchTimer = null;

function refreshProjectedGold() {
  const base = gameStore.globalAggregation;
  if (!base) {
    projectedGold.value = 0;
    return;
  }
  const projected = projectResourceStocks(
    base,
    base.rates,
    base.last_ticked_at,
    gameStore.clockOffsetMs,
    gameStore.tickIntervalMs || 60 * 60 * 1000
  );
  projectedGold.value = Math.max(0, Math.floor(Number(projected.gold) || 0));
}

const playerGold = computed(() => projectedGold.value);
const foundingTarget = computed(() =>
  process.env.NODE_ENV === "production" ? 10000 : 100
);
const userId = computed(() => Number(authStore.user?.id));
const hall = computed(() => guildStore.membership);
const hallMembers = computed(() => hall.value?.members || []);
const pendingJoins = computed(() => hall.value?.join_requests || []);
const selectedCharter = computed(() => guildStore.selectedCharter);
const selectedGuildDetail = computed(() =>
  guildModalOpen.value ? guildStore.selectedGuild : null
);
const selectedGuildId = computed(() => guildStore.selectedGuild?.id);
const charterSigners = computed(() => selectedCharter.value?.signers || []);
const isCharterCreator = computed(
  () =>
    selectedCharter.value &&
    Number(selectedCharter.value.creator_id) === userId.value
);
const canFound = computed(() => {
  const c = selectedCharter.value;
  if (!c) return false;
  return (
    Number(c.accepted_signers) >= Number(c.min_signers) &&
    Number(c.gold_collected) >= Number(c.gold_target)
  );
});
const previewIsOwnGuild = computed(() => {
  const g = selectedGuildDetail.value;
  if (!g) return false;
  if (g.my_role) return true;
  return (g.members || []).some((m) => Number(m.user_id) === userId.value);
});
const previewColspan = computed(() => (previewIsOwnGuild.value ? 4 : 2));
const hallColspan = computed(() => (guildStore.canModerate ? 6 : 5));
const canRequestJoin = computed(() => {
  const g = selectedGuildDetail.value;
  if (!g || guildStore.isMember || previewIsOwnGuild.value) return false;
  return g.my_join_status !== "pending";
});

const headerTitle = computed(() => {
  if (tab.value === "hall" && hall.value) return hall.value.name;
  if (tab.value === "charters") return "Founding Charters";
  if (tab.value === "start") return "Declare a Charter";
  return "Guild Directory";
});

const confirmTitle = computed(() => {
  if (confirmKind.value === "leave") return "Leave Guild";
  if (confirmKind.value === "cancel") return "Cancel Charter";
  if (confirmKind.value === "found") return "Incorporate Guild";
  if (confirmKind.value === "kick") return "Dismiss Member";
  return "Confirm";
});
const confirmMessage = computed(() => {
  if (confirmKind.value === "leave") {
    return "Leave this guild? If you are the last member, the house is dissolved.";
  }
  if (confirmKind.value === "cancel") {
    return "Cancel this charter? 50% of each contributor's gold will be refunded. The rest is lost.";
  }
  if (confirmKind.value === "found") {
    return "Incorporate this charter into an official guild? Founding gold is spent.";
  }
  if (confirmKind.value === "kick") {
    const name = kickTarget.value?.nickname || "this member";
    return `Dismiss ${name} from the guild?`;
  }
  return "";
});
const confirmLabel = computed(() => {
  if (confirmKind.value === "leave") return "Leave";
  if (confirmKind.value === "cancel") return "Cancel Charter";
  if (confirmKind.value === "found") return "Incorporate";
  if (confirmKind.value === "kick") return "Dismiss";
  return "Confirm";
});

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatNetWorth(value) {
  return `${formatNumber(value)} NW`;
}

function memberIsOnline(member) {
  return member?.status === "Online";
}

function raceLabel(race) {
  if (!race) return "Unknown";
  return String(race).charAt(0).toUpperCase() + String(race).slice(1);
}

function raceStyle(race) {
  return { color: getRaceColor(race), borderColor: getRaceColor(race) };
}

function roleLabel(role) {
  if (!role) return "Member";
  return String(role).charAt(0).toUpperCase() + String(role).slice(1);
}

const KICK_RANK = { leader: 0, founder: 1, officer: 2, member: 3 };

function canKickMember(member) {
  if (!member || !guildStore.canModerate) return false;
  if (Number(member.user_id) === userId.value) return false;
  const actorRank = KICK_RANK[guildStore.myRole] ?? 9;
  const targetRank = KICK_RANK[member.role] ?? 9;
  return actorRank < targetRank;
}

function askKick(member) {
  kickTarget.value = member;
  confirmKind.value = "kick";
}

function clearConfirm() {
  confirmKind.value = "";
  kickTarget.value = null;
}

function goldPct(charter) {
  const t = Number(charter?.gold_target) || 1;
  const n = Math.min(100, Math.round(((Number(charter?.gold_collected) || 0) / t) * 100));
  return `${n}%`;
}

function signerPct(charter) {
  const t = Number(charter?.max_signers) || 10;
  const n = Math.min(100, Math.round(((Number(charter?.accepted_signers) || 0) / t) * 100));
  return `${n}%`;
}

function canSign(charter) {
  if (!charter || guildStore.isMember) return false;
  if (Number(charter.creator_id) === userId.value) return false;
  if (charter.my_status === "rejected") return false;
  if (charter.my_status === "pending" || charter.my_status === "accepted") return false;
  if (guildStore.myCharter && Number(guildStore.myCharter.id) !== Number(charter.id)) {
    return false;
  }
  return (Number(charter.accepted_signers) || 0) < (Number(charter.max_signers) || 10);
}

function canContribute(charter) {
  if (!charter || charter.status !== "open") return false;
  if (Number(charter.gold_collected) >= Number(charter.gold_target)) return false;
  if (Number(charter.creator_id) === userId.value) return true;
  return charter.my_status === "accepted";
}

function flash(message, kind = "info") {
  flashMessage.value = message;
  flashKind.value = kind;
}

function apiError(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.error ||
    fallback
  );
}

async function withBusy(work) {
  if (busy.value) return;
  busy.value = true;
  try {
    await work();
  } finally {
    busy.value = false;
  }
}

async function openDirectory() {
  tab.value = "directory";
  try {
    await guildStore.fetchGuilds({
      page: guildStore.guildPage,
      search: searchText.value,
    });
  } catch {
    /* loadError on store */
  }
}

async function openCharters() {
  tab.value = "charters";
  try {
    await guildStore.fetchCharters({
      page: 1,
      search: charterSearchText.value,
    });
  } catch {
    /* loadError on store */
  }
}

async function changeCharterPage(page) {
  await withBusy(async () => {
    await guildStore.fetchCharters({
      page,
      search: charterSearchText.value,
    });
  });
}

async function runSearch() {
  await withBusy(async () => {
    await guildStore.fetchGuilds({ page: 1, search: searchText.value });
  });
}

async function changePage(page) {
  await withBusy(async () => {
    await guildStore.fetchGuilds({ page, search: searchText.value });
  });
}

async function openCharter(id) {
  await withBusy(async () => {
    await guildStore.fetchCharter(id);
    charterModalOpen.value = true;
  });
}

function closeCharter() {
  charterModalOpen.value = false;
}

async function openGuild(id) {
  await withBusy(async () => {
    await guildStore.fetchGuild(id);
    guildModalOpen.value = true;
  });
}

function closeGuild() {
  guildModalOpen.value = false;
}

function openSignModal(id) {
  signCharterId.value = id;
  signPledge.value = 0;
  signModalOpen.value = true;
}

function closeSignModal() {
  signModalOpen.value = false;
  signCharterId.value = null;
  signPledge.value = 0;
}

async function onSignConfirm() {
  const id = signCharterId.value;
  if (!id) {
    closeSignModal();
    return;
  }
  const pledge = Math.max(0, Math.min(Number(signPledge.value) || 0, playerGold.value));
  closeSignModal();
  await onSign(id, pledge);
}

async function onSign(id, pledge = 0) {
  await withBusy(async () => {
    try {
      const result = await guildStore.signCharter(id, { pledge });
      const unused = Number(result?.unused) || 0;
      const contributed = Number(result?.contributed) || 0;
      if (contributed > 0) {
        flash(
          unused > 0
            ? `Petition sent. Escrowed ${formatNumber(contributed)}g. ${formatNumber(unused)}g stayed in your purse.`
            : `Petition sent. Escrowed ${formatNumber(contributed)}g.`,
          "success"
        );
      } else {
        flash("You have applied to sign this charter.", "success");
      }
      if (charterModalOpen.value) await guildStore.fetchCharter(id);
    } catch (error) {
      flash(apiError(error, "Could not sign the charter."), "error");
    }
  });
}

async function onResolveSigner(targetId, action) {
  const charterId = selectedCharter.value?.id;
  if (!charterId) return;
  await withBusy(async () => {
    try {
      await guildStore.resolveSigner({ charterId, userId: targetId, action });
      flash(action === "accept" ? "Founder accepted." : "Application declined.", "success");
    } catch (error) {
      flash(apiError(error, "Could not update that signer."), "error");
    }
  });
}

async function onContribute() {
  const charterId = selectedCharter.value?.id;
  if (!charterId) return;
  await withBusy(async () => {
    try {
      const result = await guildStore.contribute({
        charterId,
        gold: contributeGold.value,
      });
      const unused = Number(result?.unused) || 0;
      flash(
        unused > 0
          ? `Pledged ${formatNumber(result.contributed)}g. ${formatNumber(unused)}g stayed in your purse.`
          : `Pledged ${formatNumber(result.contributed)}g.`,
        "success"
      );
    } catch (error) {
      flash(apiError(error, "Could not contribute gold."), "error");
    }
  });
}

async function onCreate() {
  await withBusy(async () => {
    try {
      const result = await guildStore.createCharter({
        name: formName.value,
        description: formDescription.value,
        gold: formGold.value || 0,
      });
      const unused = Number(result?.unused) || 0;
      flash(
        unused > 0
          ? `Charter declared. ${formatNumber(unused)}g of your pledge was not needed.`
          : "Charter declared.",
        "success"
      );
      formName.value = "";
      formDescription.value = "";
      formGold.value = 0;
      tab.value = "charters";
      if (result?.charter?.id) {
        await guildStore.fetchCharter(result.charter.id);
        charterModalOpen.value = true;
      }
      await guildStore.fetchCharters();
    } catch (error) {
      flash(apiError(error, "Could not declare the charter."), "error");
    }
  });
}

async function onJoin() {
  const id = selectedGuildDetail.value?.id;
  if (!id) return;
  await withBusy(async () => {
    try {
      await guildStore.requestJoin(id);
      flash("Join request sent.", "success");
    } catch (error) {
      flash(apiError(error, "Could not send a join request."), "error");
    }
  });
}

async function onResolveJoin(targetId, action) {
  const guildId = hall.value?.id;
  if (!guildId) return;
  await withBusy(async () => {
    try {
      await guildStore.resolveJoin({ guildId, userId: targetId, action });
      await guildStore.fetchMy();
      flash(action === "accept" ? "Member accepted." : "Request declined.", "success");
    } catch (error) {
      flash(apiError(error, "Could not update that request."), "error");
    }
  });
}

async function onConfirm() {
  const kind = confirmKind.value;
  const target = kickTarget.value;
  clearConfirm();
  if (kind === "leave") {
    await withBusy(async () => {
      try {
        const result = await guildStore.leaveGuild();
        flash(
          result?.dissolved ? "The guild has been dissolved." : "You have left the guild.",
          "info"
        );
        tab.value = "directory";
        await openDirectory();
      } catch (error) {
        flash(apiError(error, "Could not leave the guild."), "error");
      }
    });
    return;
  }
  if (kind === "cancel") {
    const id = selectedCharter.value?.id;
    if (!id) return;
    await withBusy(async () => {
      try {
        await guildStore.cancelCharter(id);
        charterModalOpen.value = false;
        flash("Charter cancelled. 50% of contributed gold has been returned.", "info");
      } catch (error) {
        flash(apiError(error, "Could not cancel the charter."), "error");
      }
    });
    return;
  }
  if (kind === "found") {
    const id = selectedCharter.value?.id;
    if (!id) return;
    await withBusy(async () => {
      try {
        await guildStore.foundCharter(id);
        charterModalOpen.value = false;
        tab.value = "hall";
        flash("The guild has been incorporated.", "success");
      } catch (error) {
        flash(apiError(error, "Could not incorporate the guild."), "error");
      }
    });
    return;
  }
  if (kind === "kick") {
    const guildId = hall.value?.id;
    const targetUserId = target?.user_id;
    if (!guildId || !targetUserId) return;
    await withBusy(async () => {
      try {
        await guildStore.kickMember({ guildId, targetUserId });
        await guildStore.fetchMy();
        flash(`${target.nickname || "Member"} has been dismissed.`, "info");
      } catch (error) {
        flash(apiError(error, "Could not dismiss that member."), "error");
      }
    });
  }
}

watch(
  () => [gameStore.globalAggregation, gameStore.clockOffsetMs],
  () => {
    refreshProjectedGold();
  },
  { immediate: true, deep: true }
);

watch(charterSearchText, () => {
  if (tab.value !== "charters") return;
  clearTimeout(charterSearchTimer);
  charterSearchTimer = setTimeout(() => {
    guildStore.fetchCharters({ page: 1, search: charterSearchText.value }).catch(() => {});
  }, 300);
});

onMounted(async () => {
  refreshProjectedGold();
  goldTicker = setInterval(refreshProjectedGold, 1000);
  try {
    await guildStore.fetchMy();
    if (guildStore.isMember) {
      tab.value = "hall";
    } else if (guildStore.myCharter) {
      tab.value = "charters";
      await guildStore.fetchCharters({ page: 1, search: charterSearchText.value });
    } else {
      await guildStore.fetchGuilds({ page: 1 });
    }
  } catch (error) {
    flash(apiError(error, "Could not load guild records."), "error");
  }
});

onUnmounted(() => {
  if (goldTicker) clearInterval(goldTicker);
  clearTimeout(charterSearchTimer);
});
</script>

<style scoped>
.guild-view {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 1.25rem 1.5rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.guild-window {
  width: min(1080px, 100%);
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 246, 255, 0.45);
  box-shadow:
    0 0 0 1px rgba(212, 175, 55, 0.2),
    0 18px 40px rgba(0, 0, 0, 0.45);
  color: #e8eef8;
  padding: 1.1rem 1.25rem 1.25rem;
  border-radius: 10px;
}

.guild-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
}

.guild-kicker {
  margin: 0 0 0.2rem;
  font-size: 0.72rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #c8b56a;
}

.guild-header h1 {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 1.45rem;
  color: #f4e6c4;
}

.guild-gold {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.gold-label {
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c8b56a;
}

.gold-value {
  font-variant-numeric: tabular-nums;
  color: #ffd700;
  font-size: 1.15rem;
}

.guild-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.85rem 0 0.65rem;
}

.guild-tab {
  font-family: "Cinzel", serif;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.38rem 0.75rem;
  background: transparent;
  color: #a9b1d6;
  border: 1px solid rgba(0, 246, 255, 0.28);
  border-radius: 999px;
  cursor: pointer;
}

.guild-tab.active,
.guild-tab:hover {
  color: #00f6ff;
  border-color: #00f6ff;
  box-shadow: 0 0 10px rgba(0, 246, 255, 0.28);
}

.guild-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.flash {
  margin: 0 0 0.5rem;
  font-size: 0.88rem;
  color: #88c0d0;
}

.flash.success {
  color: #9ae6b4;
}

.flash.error {
  color: #feb2b2;
}

.lede-copy,
.meta-line,
.empty {
  margin: 0;
  color: #88c0d0;
  line-height: 1.5;
}

.hall-lede h2,
.list-card h3 {
  margin: 0 0 0.35rem;
  font-family: "Cinzel", serif;
  color: #f4e6c4;
}

.filter-bar {
  display: flex;
  gap: 0.5rem;
}

.charter-notice {
  margin: 0;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(212, 175, 55, 0.55);
  background: rgba(212, 175, 55, 0.12);
  color: #f4e6c4;
  line-height: 1.5;
  font-size: 0.88rem;
}

.sign-pledge-label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.75rem;
  color: #c8b56a;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.sign-pledge-label input {
  background: rgba(8, 12, 28, 0.7);
  border: 1px solid rgba(0, 246, 255, 0.28);
  color: #e8eef8;
  padding: 0.45rem 0.65rem;
  border-radius: 6px;
}

.filter-search,
.start-form input,
.start-form textarea,
.contribute-row input {
  flex: 1;
  background: rgba(8, 12, 28, 0.7);
  border: 1px solid rgba(0, 246, 255, 0.28);
  color: #e8eef8;
  padding: 0.45rem 0.65rem;
  border-radius: 6px;
}

.card-list,
.person-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.list-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(0, 246, 255, 0.18);
  background: rgba(8, 12, 28, 0.45);
  cursor: pointer;
}

.list-card:hover,
.list-card.selected {
  border-color: rgba(0, 246, 255, 0.55);
}

.card-wide {
  width: 100%;
}

.inbox-panel {
  border: 1px solid rgba(212, 175, 55, 0.35);
  padding: 0.85rem 1rem;
  background: rgba(212, 175, 55, 0.06);
}

.inbox-panel h3,
.guild-body h3 {
  margin: 0 0 0.55rem;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c9b48a;
}

.person-id {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.person-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 0;
}

.race-badge {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.05rem 0.4rem;
  border: 1px solid;
  border-radius: 999px;
  font-size: 0.7rem;
  letter-spacing: 0.04em;
}

.guild-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;
}

.guild-table th,
.guild-table td {
  padding: 0.4rem 0.35rem;
  text-align: left;
  border-bottom: 1px solid rgba(0, 246, 255, 0.12);
}

.online-pip {
  display: inline-block;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: #4a5568;
  margin-right: 0.35rem;
}

.online-pip.on {
  background: #38a169;
  box-shadow: 0 0 8px rgba(56, 161, 105, 0.7);
}

.bars {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.55rem;
}

.bar-block span {
  display: block;
  font-size: 0.75rem;
  color: #a9b1d6;
  margin-bottom: 0.2rem;
}

.bar {
  height: 8px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(0, 246, 255, 0.2);
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
}

.bar-fill.gold {
  background: linear-gradient(90deg, #b7791f, #ffd700);
}

.bar-fill.cyan {
  background: linear-gradient(90deg, #2b6cb0, #00f6ff);
}

.row-actions,
.contribute-row,
.detail-footer,
.hall-footer,
.pager {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
}

.hall-footer,
.detail-footer {
  margin-top: 0.75rem;
}

.charter-card-actions {
  margin-top: 0.85rem;
}

.guild-btn {
  font-family: "Exo 2", sans-serif;
  background: transparent;
  color: #00f6ff;
  border: 1px solid #00f6ff;
  border-radius: 6px;
  padding: 0.4rem 1rem;
  cursor: pointer;
}

.guild-btn.gold {
  color: #ffd700;
  border-color: #d4af37;
}

.guild-btn.ghost {
  color: #a9b1d6;
  border-color: #556c88;
}

.guild-btn.danger {
  color: #feb2b2;
  border-color: #e53e3e;
}

.guild-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.start-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 32rem;
}

.start-form label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.82rem;
  color: #c9b48a;
}

.detail-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 0.75rem;
}

.detail-container {
  width: min(920px, 96vw);
  max-height: min(90vh, 90dvh);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: rgba(15, 23, 42, 0.97);
  border: 1px solid #00f6ff;
  border-radius: 10px;
  box-shadow: 0 0 30px rgba(0, 246, 255, 0.45);
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0, 246, 255, 0.3);
}

.detail-header h2 {
  margin: 0;
  font-family: "Orbitron", sans-serif;
  color: #00f6ff;
  font-size: 1.05rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #a9b1d6;
  cursor: pointer;
}

.close-btn:hover {
  color: #ff00ff;
}

.detail-body {
  padding: 1rem 1.25rem 1.25rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .guild-view {
    padding: 0.75rem 0.75rem 1rem;
  }
  .guild-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
